<template>
  <div class="container mt-4">
    <TopNav :showOpenMainNav="true">
      <NavItem
        v-if="!maxTeamSizeReached"
        icon="fal fa-plus"
        :text="$t('SETTINGS.BUTTON.ADD_ADMIN')"
        @click.native="addAdmin"
      />
    </TopNav>
    <div class="mt-4">
      <h1>{{ $t('SETTINGS.TITLE.ADMINS') }}</h1>
      <HMSearch v-model="search" class="mt-4" />
      <HMTable
        :fields="tableFields"
        :items="combinedUsersAndInvitations"
        class="mt-4"
      >
        <template slot="name" slot-scope="{ data }">
          {{ data.name }}
        </template>
        <template slot="status" slot-scope="{ data }">
          <HMStatus
            :active="data.activated"
            :activeText="
              data.isOwner
                ? $t('SETTINGS.LABEL.OWNER')
                : $t('SETTINGS.LABEL.ACCESS_GRANTED')
            "
            :inactiveText="$t('SETTINGS.LABEL.ACCESS_PENDING')"
          />
        </template>
        <template slot="edit" slot-scope="{ data }">
          <HMIconButton
            v-if="!data.isOwner"
            :icon="['far', 'ellipsis-v']"
            :id="`popover-${data.id}`"
          />
          <HMPopover :target="`popover-${data.id}`" boundary-padding="-20">
            <HMPopoverMenuItem
              @click="removeTeamMember(data.id)"
              v-if="data.isUser && !data.isOwner"
            >
              {{ $t('SETTINGS.LABEL.REMOVE_ACCESS') }}
            </HMPopoverMenuItem>
            <HMPopoverMenuItem
              @click="removeInvitation(data.id)"
              v-if="data.isInvitation"
            >
              {{ $t('SETTINGS.LABEL.REMOVE_INVITATION') }}
            </HMPopoverMenuItem>
          </HMPopover>
        </template>
      </HMTable>
      <div class="mt-4" v-if="maxTeamSizeReached">
        <p>
          <span class="mr-2">{{
            $t('SETTINGS.TEXT.TEAM_SIZE_LIMIT_REACHED', {
              maxTeamSize: maxAdmins + 1,
            })
          }}</span>
          <router-link :to="{ name: 'settings-adjust-plan' }">
            {{ $t('SETTINGS.TEXT.UPGRADE_TO_ADD_MORE') }}
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import TopNav from '../../../components/navigation/TopNav';
import NavItem from '../../../components/navigation/NavItem';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import HMUserInvitationModal from '@/web/components/common/HMUserInvitationModal';

export default {
  name: 'ManageAdmins',
  components: {
    TopNav,
    NavItem,
  },
  computed: {
    ...mapGetters({
      usersAndInvitations: 'user/usersAndInvitations',
      maxAdmins: 'user/maxAdmins',
    }),
    maxTeamSizeReached() {
      if (this.combinedUsersAndInvitations) {
        // +1 is to account for the owner who is also an admin
        return this.combinedUsersAndInvitations.length >= this.maxAdmins + 1;
      }
      return false;
    },
    combinedUsersAndInvitations() {
      const result = [];
      if (this.usersAndInvitations) {
        this.usersAndInvitations?.users.forEach((user, index) => {
          result.push({
            id: user.user_id,
            name: user.user_full_name,
            isOwner: index === 0,
            activated: true,
            email: user.user_email,
            isUser: true,
          });
        });
        this.usersAndInvitations?.invitations.forEach(invitation => {
          result.push({
            id: invitation.unique_id,
            name: '-',
            activated: false,
            email: invitation.email,
            isInvitation: true,
          });
        });
      }
      return result;
    },
  },
  data() {
    return {
      search: '',
      tableFields: [
        {
          name: 'name',
          title: this.$t('SETTINGS.LABEL.NAME'),
          cssClass: 'h5',
        },
        {
          name: 'status',
          title: this.$t('SETTINGS.LABEL.STATUS'),
        },
        {
          key: 'email',
          title: this.$t('SETTINGS.LABEL.EMAIL'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
        },
      ],
    };
  },
  methods: {
    addAdmin() {
      this.$modal.show(HMUserInvitationModal, {
        onSelect: this.sendAdminInvitation,
      });
    },
    async sendAdminInvitation(email) {
      try {
        await this.$store.dispatch('user/inviteAdmin', email);
        this.loadData();
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('NOTIFICATIONS.TEXT.INVITATION_SUCCESSFULLY_CREATED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to send an invitation',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
        });
      }
    },
    async removeInvitation(invitationUniqueId) {
      try {
        await this.$store.dispatch('user/removeInvitation', invitationUniqueId);
        this.loadData();
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('NOTIFICATIONS.TEXT.INVITATION_SUCCESSFULLY_REMOVED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to remove an invitation',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
        });
      }
    },
    async removeTeamMember(userId) {
      try {
        await this.$store.dispatch('user/removeTeamMember', userId);
        this.loadData();
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('NOTIFICATIONS.TEXT.TEAM_MEMBER_SUCCESSFULLY_REMOVED'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to remove a team member',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
        });
      }
    },
    loadData() {
      try {
        this.$store.dispatch('user/getStudioUsersAndInvitations');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load users and invitations',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
        });
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style lang="scss" scoped></style>
